import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter, Redirect } from 'react-router-dom';
import Cookies from 'js-cookie';
import classNames from 'classnames';
import { isEmpty } from 'lodash';

import { pathByRouteName } from '../../util/routes';
import { apiBaseUrl } from '../../util/api';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { ensureCurrentUser } from '../../util/data';
import {
  isSignupEmailTakenError,
  isTooManyEmailVerificationRequestsError,
} from '../../util/errors';
import {
  Page,
  NamedLink,
  NamedRedirect,
  LinkTabNavHorizontal,
  IconEmailSent,
  InlineTextButton,
  SocialLoginButton,
  LayoutSingleColumn,
  CloseButton,
  TermsAndConditions,
  PrivacyPolicy,
  Logo,
} from '../../components';
import { login, authenticationInProgress, signup, signupWithIdp } from '../../ducks/auth.duck';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/ui.duck';
import { sendVerificationEmail } from '../../ducks/user.duck';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './AuthenticationPage.module.css';
import { FacebookLogo, GoogleLogo } from './socialLoginLogos';
import routeConfiguration from '../../routing/routeConfiguration';
import LoginForm from './LoginForm/LoginForm';
import SignupForm from './SignupForm/SignupForm';
import ConfirmSignupForm from './ConfirmSignupForm/ConfirmSignupForm';
import { useConfiguration } from '../../context/configurationContext';

const KEY_CODE_ENTER = 13;
const SIGNUP_DISCOUNT_COOKIE = 'st-signupDiscount';
const SIGNUP_DISCOUNT_PENDING_KEY = 'crion-welcome-discount-pending';
const HOMEPAGE_SIGNUP_SOURCE = 'homepage';

const getSignupDiscountFromSearch = search => {
  const searchParams = new URLSearchParams(search || '');
  const discountCode = searchParams.get('code');
  const discountSource = searchParams.get('source');

  if (!discountCode || discountSource !== HOMEPAGE_SIGNUP_SOURCE) {
    return null;
  }

  return {
    discountCode: discountCode.trim().toUpperCase(),
    discountSource,
  };
};

const getSignupDiscountFromCookie = () => {
  const cookieValue = Cookies.get(SIGNUP_DISCOUNT_COOKIE);

  if (!cookieValue) {
    return null;
  }

  try {
    const parsedValue = JSON.parse(cookieValue.replace('j:', ''));
    return parsedValue?.discountCode && parsedValue?.discountSource === HOMEPAGE_SIGNUP_SOURCE
      ? parsedValue
      : null;
  } catch (e) {
    Cookies.remove(SIGNUP_DISCOUNT_COOKIE);
    return null;
  }
};

const getSignupDiscount = location => {
  return getSignupDiscountFromSearch(location?.search) || getSignupDiscountFromCookie();
};

const persistSignupDiscount = signupDiscount => {
  if (signupDiscount?.discountCode) {
    Cookies.set(SIGNUP_DISCOUNT_COOKIE, JSON.stringify(signupDiscount), { expires: 1 / 24 });
    if (typeof window !== 'undefined' && window.sessionStorage) {
      try {
        window.sessionStorage.setItem(SIGNUP_DISCOUNT_PENDING_KEY, signupDiscount.discountCode);
      } catch (e) {
        // Ignore storage errors; the signup payload still carries the discount.
      }
    }
  }
};

const getSignupIntentFromSearch = search => {
  const searchParams = new URLSearchParams(search || '');
  const intent = searchParams.get('intent');

  return intent === 'customer' || intent === 'provider' ? intent : null;
};

export class AuthenticationPageComponent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      tosModalOpen: false,
      privacyModalOpen: false,
      signupUserIntent: getSignupIntentFromSearch(props.location?.search),
      authError: Cookies.get('st-autherror')
        ? JSON.parse(Cookies.get('st-autherror').replace('j:', ''))
        : null,
      authInfo: Cookies.get('st-authinfo')
        ? JSON.parse(Cookies.get('st-authinfo').replace('j:', ''))
        : null,
    };
  }

  componentDidMount() {
    persistSignupDiscount(getSignupDiscount(this.props.location));

    // Remove the autherror cookie once the content is saved to state
    // because we don't want to show the error message e.g. after page refresh
    Cookies.remove('st-autherror');
    
    // Check if we're on confirm page after OAuth callback and restore background location
    const { location, history, tab } = this.props;
    const savedBgLocation = Cookies.get('st-bgLocation');
    
    if (tab === 'confirm' && savedBgLocation && !location.state?.backgroundLocation) {
      // We're returning from OAuth callback - restore the background location
      try {
        const bgLocation = JSON.parse(savedBgLocation.replace('j:', ''));
        Cookies.remove('st-bgLocation');
        
        // Replace current location with modal state
        history.replace(location.pathname + location.search, {
          modal: true,
          backgroundLocation: bgLocation
        });
      } catch (e) {
        console.error('Failed to parse saved background location:', e);
        Cookies.remove('st-bgLocation');
      }
    }
    
    // Add ESC key listener to close modal
    this.handleEscKey = (e) => {
      if (e.key === 'Escape' || e.keyCode === 27) {
        const backgroundLocation = location.state?.backgroundLocation;
        if (backgroundLocation) {
          history.goBack();
        }
      }
    };
    window.addEventListener('keydown', this.handleEscKey);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.location?.search !== this.props.location?.search) {
      persistSignupDiscount(getSignupDiscount(this.props.location));
    }
  }

  componentWillUnmount() {
    // Remove ESC key listener
    if (this.handleEscKey) {
      window.removeEventListener('keydown', this.handleEscKey);
    }
  }

  render() {
    const {
      authInProgress,
      currentUser,
      intl,
      isAuthenticated,
      location,
      loginError,
      scrollingDisabled,
      signupError,
      submitLogin,
      submitSignup,
      confirmError,
      submitSingupWithIdp,
      tab,
      sendVerificationEmailInProgress,
      sendVerificationEmailError,
      onResendVerificationEmail,
      config,
      history,
    } = this.props;

    const isConfirm = tab === 'confirm';
    const isLogin = tab === 'login';
    const locationFrom = location.state && location.state.from ? location.state.from : null;
    const authinfoFrom =
      this.state.authInfo && this.state.authInfo.from ? this.state.authInfo.from : null;
    
    // Fall back to backgroundLocation if 'from' is not explicitly set (modal auth flow)
    const backgroundLocation = location.state?.backgroundLocation;
    const backgroundFrom = backgroundLocation
      ? `${backgroundLocation.pathname}${backgroundLocation.search || ''}${backgroundLocation.hash || ''}`
      : null;
    
    const from = locationFrom || authinfoFrom || backgroundFrom || null;
    const signupDiscount = getSignupDiscount(location);
    const signupIntent = getSignupIntentFromSearch(location.search);
    
    // Detect if user is coming from checkout or listing page (customer context)
    const isCustomerContext = from && (from.includes('/checkout') || from.includes('/l/'));

    const user = ensureCurrentUser(currentUser);
    const currentUserLoaded = !!user.id;

    // We only want to show the email verification dialog in the signup
    // tab if the user isn't being redirected somewhere else
    // (i.e. `from` is present). We must also check the `emailVerified`
    // flag only when the current user is fully loaded.
    const showEmailVerification = !isLogin && currentUserLoaded && !user.attributes.emailVerified;

    // Already authenticated, redirect away from auth page
    if (isAuthenticated && from) {
      return <Redirect to={from} />;
    } else if (isAuthenticated && currentUserLoaded && !showEmailVerification) {
      return <NamedRedirect name="LandingPage" />;
    }

    const loginContentClass = classNames(css.content, css.loginContent);

    const loginErrorMessage = (
      <div className={css.error}>
        <FormattedMessage id="AuthenticationPage.loginFailed" />
      </div>
    );

    const signupErrorMessage = (
      <div className={css.error}>
        {isSignupEmailTakenError(signupError) ? (
          <FormattedMessage id="AuthenticationPage.signupFailedEmailAlreadyTaken" />
        ) : (
          <FormattedMessage id="AuthenticationPage.signupFailed" />
        )}
      </div>
    );

    const confirmErrorMessage = confirmError ? (
      <div className={css.error}>
        {isSignupEmailTakenError(confirmError) ? (
          <FormattedMessage id="AuthenticationPage.signupFailedEmailAlreadyTaken" />
        ) : (
          <FormattedMessage id="AuthenticationPage.signupFailed" />
        )}
      </div>
    ) : null;

    // eslint-disable-next-line no-confusing-arrow
    const errorMessage = (error, message) => (error ? message : null);
    const loginOrSignupError = isLogin
      ? errorMessage(loginError, loginErrorMessage)
      : errorMessage(signupError, signupErrorMessage);

    const fromState = { state: from ? { from } : null };
    const bgLocation = location.state?.backgroundLocation;
    const modalState = bgLocation ? {
      state: {
        modal: true,
        backgroundLocation: bgLocation,
        ...(from ? { from } : {})
      }
    } : fromState;

    const tabs = [
      {
        text: (
          <h1 className={css.tab}>
            <FormattedMessage id="AuthenticationPage.signupLinkText" />
          </h1>
        ),
        selected: !isLogin,
        linkProps: {
          name: 'SignupPage',
          to: modalState,
        },
      },
      {
        text: (
          <h1 className={css.tab}>
            <FormattedMessage id="AuthenticationPage.loginLinkText" />
          </h1>
        ),
        selected: isLogin,
        linkProps: {
          name: 'LoginPage',
          to: modalState,
        },
      },
    ];

    const handleSubmitSignup = values => {
      const { email, password, fname, lname, accountType, cname, userIntent } = values;
      const isCompanyAccount = accountType === 'certifiedBusiness';
      const isProvider = userIntent === 'provider';
      const shouldScrollAfterSignup = !location.state?.backgroundLocation;
      const signupDiscountParams = signupDiscount || {};
      const submitSignupWithDiscount = signupParams =>
        submitSignup({ ...signupParams, ...signupDiscountParams });

      if (isProvider && isCompanyAccount) {
        const companyName = cname.trim();

        return submitSignupWithDiscount({
          email,
          password,
          firstName: companyName,
          lastName: '.',
          displayName: companyName,
          publicData: {
            isCompany: true,
            companyName,
          },
          userIntent: userIntent || 'customer',
        });
      }

      const firstName = fname.trim();
      const lastName = lname.trim();

      submitSignupWithDiscount({
        email, 
        password, 
        firstName, 
        lastName, 
        displayName: firstName,
        publicData: isProvider ? { isCompany: false } : {},
        userIntent: userIntent || 'customer',
      }).then(() =>
        shouldScrollAfterSignup && typeof window !== 'undefined'
          ? window.scrollTo({ top: 0, behavior: 'smooth' })
          : null
      );
    };

    const handleSubmitConfirm = values => {
      const { idpToken, email, firstName, lastName, idpId, userIntent } = this.state.authInfo;
      const { email: newEmail, firstName: newFirstName, lastName: newLastName, accountType, companyName, ...rest } = values;
      const signupDiscountParams = signupDiscount || {};
      const submitIdpWithDiscount = signupParams =>
        submitSingupWithIdp({ ...signupParams, ...signupDiscountParams });

      // Pass email, fistName or lastName to Flex API only if user has edited them
      // sand they can't be fetched directly from idp provider (e.g. Facebook)

      const isProvider = userIntent === 'provider';
      const isCompanyAccount = accountType === 'certifiedBusiness';

      // Handle company account
      if (isProvider && isCompanyAccount && companyName) {
        const trimmedCompanyName = companyName.trim();
        
        submitIdpWithDiscount({
          idpToken,
          idpId,
          email: newEmail || email,
          firstName: trimmedCompanyName,
          lastName: '.',
          displayName: trimmedCompanyName,
          publicData: {
            isCompany: true,
            companyName: trimmedCompanyName,
          },
          privateData: { userIntent: userIntent || 'customer' },
        });
        return;
      }

      // Handle individual account
      const authParams = {
        ...(newEmail !== email && { email: newEmail }),
        ...(newFirstName !== firstName && { firstName: newFirstName }),
        ...(newLastName !== lastName && { lastName: newLastName }),
      };

      const displayName = newFirstName || firstName;

      // If the confirm form has any additional values, pass them forward as user's protected data
      const protectedData = !isEmpty(rest) ? { ...rest } : null;

      // Store userIntent in privateData
      const privateData = { userIntent: userIntent || 'customer' };
      const publicData = isProvider ? { isCompany: isCompanyAccount } : {};

      submitIdpWithDiscount({
        idpToken,
        idpId,
        displayName,
        ...authParams,
        ...(!!protectedData && { protectedData }),
        ...(!!privateData && { privateData }),
        ...(!!publicData && { publicData }),
        metadata: { userType: 'crionUser' },
      });
    };

    const getDefaultRoutes = () => {
      const routes = routeConfiguration();
      const baseUrl = apiBaseUrl();

      // Route where the user should be returned after authentication
      // This is used e.g. with EditListingPage and ListingPage
      const fromParam = from ? `from=${from}` : '';

      // Default route where user is returned after successfull authentication
      const defaultReturn = pathByRouteName('LandingPage', routes);
      const defaultReturnParam = defaultReturn ? `&defaultReturn=${defaultReturn}` : '';

      // Route for confirming user data before creating a new user
      const defaultConfirm = pathByRouteName('ConfirmPage', routes);
      const defaultConfirmParam = defaultConfirm ? `&defaultConfirm=${defaultConfirm}` : '';

      return { baseUrl, fromParam, defaultReturnParam, defaultConfirmParam };
    };
    const authWithFacebook = () => {
      const defaultRoutes = getDefaultRoutes();
      const { baseUrl, fromParam, defaultReturnParam, defaultConfirmParam } = defaultRoutes;
      const userIntentParam = this.state.signupUserIntent ? `&userIntent=${this.state.signupUserIntent}` : '';
      
      // Clear any signup errors before redirecting
      if (signupError) {
        this.props.onManageDisableScrolling('AuthenticationPage.signupError', false);
      }
      
      // Store backgroundLocation in cookie for OAuth callback
      const backgroundLocation = location.state?.backgroundLocation;
      if (backgroundLocation) {
        Cookies.set('st-bgLocation', JSON.stringify({
          pathname: backgroundLocation.pathname,
          search: backgroundLocation.search || ''
        }), { expires: 1/24 }); // 1 hour expiry
      }

      persistSignupDiscount(signupDiscount);
      
      window.location.href = `${baseUrl}/api/auth/facebook?${fromParam}${defaultReturnParam}${defaultConfirmParam}${userIntentParam}`;
    };

    const authWithGoogle = () => {
      const defaultRoutes = getDefaultRoutes();
      const { baseUrl, fromParam, defaultReturnParam, defaultConfirmParam } = defaultRoutes;
      const userIntentParam = this.state.signupUserIntent ? `&userIntent=${this.state.signupUserIntent}` : '';
      
      // Clear any signup errors before redirecting
      if (signupError) {
        this.props.onManageDisableScrolling('AuthenticationPage.signupError', false);
      }
      
      // Store backgroundLocation in cookie for OAuth callback
      const backgroundLocation = location.state?.backgroundLocation;
      if (backgroundLocation) {
        Cookies.set('st-bgLocation', JSON.stringify({
          pathname: backgroundLocation.pathname,
          search: backgroundLocation.search || ''
        }), { expires: 1/24 }); // 1 hour expiry
      }

      persistSignupDiscount(signupDiscount);
      
      window.location.href = `${baseUrl}/api/auth/google?${fromParam}${defaultReturnParam}${defaultConfirmParam}${userIntentParam}`;
    };

    const idp = this.state.authInfo
      ? this.state.authInfo.idpId.replace(/^./, str => str.toUpperCase())
      : null;

    // Form for confirming information frm IdP (e.g. Facebook)
    // before new user is created to Flex
    const confirmForm = (
      <div className={css.content}>
        <CloseButton 
          onClick={() => {
            const backgroundLocation = location.state?.backgroundLocation;
            if (backgroundLocation) {
              history.push(backgroundLocation.pathname + backgroundLocation.search);
            } else {
              history.push('/');
            }
          }} 
          className={css.closeAuth} 
        />
        <h1 className={css.signupWithIdpTitle}>
          <FormattedMessage id="AuthenticationPage.confirmSignupWithIdpTitle" values={{ idp }} />
        </h1>

        <p className={css.confirmInfoText}>
          <FormattedMessage id="AuthenticationPage.confirmSignupInfoText" />
        </p>
        {confirmErrorMessage}
        <ConfirmSignupForm
          className={css.form}
          onSubmit={handleSubmitConfirm}
          inProgress={authInProgress}
          onOpenTermsOfService={() => this.setState({ tosModalOpen: true })}
          onOpenPrivacyPolicy={() => this.setState({ privacyModalOpen: true })}
          authInfo={this.state.authInfo}
          idp={idp}
        />
      </div>
    );

    // Social login buttons
    const showFacebookLogin = !!process.env.REACT_APP_FACEBOOK_APP_ID;
    const showGoogleLogin = !!process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const showSocialLogins = showFacebookLogin || showGoogleLogin;

    const facebookButtonText = isLogin ? (
      <FormattedMessage id="AuthenticationPage.loginWithFacebook" />
    ) : (
      <FormattedMessage id="AuthenticationPage.signupWithFacebook" />
    );

    const googleButtonText = isLogin ? (
      <FormattedMessage id="AuthenticationPage.loginWithGoogle" />
    ) : (
      <FormattedMessage id="AuthenticationPage.signupWithGoogle" />
    );
    // Social login buttons - remove the "Or" divider, it will be shown in the form
    const socialLoginButtonsMaybe = showSocialLogins ? (
      <div className={css.idpButtons}>
        {showGoogleLogin ? (
          <div className={css.socialButtonWrapper}>
            <SocialLoginButton onClick={() => authWithGoogle()}>
              <span className={css.buttonIcon}>{GoogleLogo}</span>
              {googleButtonText}
            </SocialLoginButton>
          </div>
        ) : null}

        {showFacebookLogin ? (
          <div className={css.socialButtonWrapper}>
            <SocialLoginButton onClick={() => authWithFacebook()}>
              <span className={css.buttonIcon}>{FacebookLogo}</span>
              {facebookButtonText}
            </SocialLoginButton>
          </div>
        ) : null}
      </div>
    ) : null;

    const onOpenTermsOfService = () => {
      this.setState({ tosModalOpen: true });
      window && window.scrollTo(0, 0);
    };

    const onOpenPrivacyPolicy = () => {
      this.setState({ privacyModalOpen: true });
      window && window.scrollTo(0, 0);
    };

    const handleTermsKeyUp = e => {
      // Allow click action with keyboard like with normal links
      if (e.keyCode === KEY_CODE_ENTER) {
        onOpenTermsOfService();
      }
    };

    const termsLink = (
      <span
        className={css.termsLink}
        onClick={onOpenTermsOfService}
        role="button"
        tabIndex="0"
        onKeyUp={handleTermsKeyUp}
      >
        <FormattedMessage id="SignupForm.termsAndConditionsLinkText" />
      </span>
    );

    const privacyLink = (
      <span
        className={css.termsLink}
        onClick={onOpenPrivacyPolicy}
        role="button"
        tabIndex="0"
        onKeyUp={e => e.keyCode === KEY_CODE_ENTER && onOpenPrivacyPolicy()}
      >
        <FormattedMessage id="SignupForm.privacyPolicyLinkText" />
      </span>
    );

    // Tabs for SignupForm and LoginForm
    const authenticationForms = (
      <div className={loginContentClass}>
        <CloseButton 
          onClick={() => {
            const backgroundLocation = location.state?.backgroundLocation;
            if (backgroundLocation) {
              history.push(backgroundLocation.pathname + backgroundLocation.search);
            } else {
              history.push('/');
            }
          }} 
          className={css.closeAuth} 
        />
        <Logo className={css.logo} format="desktop" />
        <LinkTabNavHorizontal className={css.tabs} tabs={tabs} />
        {loginOrSignupError}

        {isLogin ? (
          <LoginForm className={css.loginForm} onSubmit={submitLogin} inProgress={authInProgress} />
        ) : (
          <SignupForm
            className={css.signupForm}
            onSubmit={handleSubmitSignup}
            inProgress={authInProgress}
            initialValues={{
              isCompany: 'false',
              ...((signupIntent || isCustomerContext) && { userIntent: signupIntent || 'customer' }),
            }}
            onFormChange={(values) => {
              // Track userIntent to show/hide social logins
              this.setState({ signupUserIntent: values?.userIntent });
            }}
            socialLoginButtons={(isLogin || this.state.signupUserIntent) ? socialLoginButtonsMaybe : null}
          />
        )}

        {/* Show social logins for login page */}
        {isLogin && socialLoginButtonsMaybe}

        {/* Show terms only after user has made relevant selections */}
        {!isLogin && this.state.signupUserIntent && (
          <p className={css.bottomWrapperText}>
            <span className={css.termsText}>
              <FormattedMessage
                id="SignupForm.termsAndConditionsAcceptText"
                values={{ termsLink, privacyLink }}
              />
            </span>
          </p>
        )}
      </div>
    );

    const formContent = isConfirm ? confirmForm : authenticationForms;

    const name = user.attributes.profile.firstName;
    const email = <span className={css.email}>{user.attributes.email}</span>;

    const resendEmailLink = (
      <InlineTextButton rootClassName={css.modalHelperLink} onClick={onResendVerificationEmail}>
        <FormattedMessage id="AuthenticationPage.resendEmailLinkText" />
      </InlineTextButton>
    );
    const fixEmailLink = (
      <NamedLink className={css.modalHelperLink} name="ContactDetailsPage">
        <FormattedMessage id="AuthenticationPage.fixEmailLinkText" />
      </NamedLink>
    );

    const resendErrorTranslationId = isTooManyEmailVerificationRequestsError(
      sendVerificationEmailError
    )
      ? 'AuthenticationPage.resendFailedTooManyRequests'
      : 'AuthenticationPage.resendFailed';
    const resendErrorMessage = sendVerificationEmailError ? (
      <p className={css.error}>
        <FormattedMessage id={resendErrorTranslationId} />
      </p>
    ) : null;

    const emailVerificationContent = (
      <div className={css.content}>
        <CloseButton linkTo={'LoginPage'} />
        <IconEmailSent className={css.modalIcon} />
        <h1 className={css.modalTitle}>
          <FormattedMessage id="AuthenticationPage.verifyEmailTitle" values={{ name }} />
        </h1>
        <p className={css.modalMessage}>
          <FormattedMessage id="AuthenticationPage.verifyEmailText" values={{ email }} />
        </p>
        {resendErrorMessage}

        <div className={css.bottomWrapper}>
          <p className={css.modalHelperText}>
            {sendVerificationEmailInProgress ? (
              <FormattedMessage id="AuthenticationPage.sendingEmail" />
            ) : (
              <FormattedMessage id="AuthenticationPage.resendEmail" values={{ resendEmailLink }} />
            )}
          </p>
          <p className={css.modalHelperText}>
            <FormattedMessage id="AuthenticationPage.fixEmail" values={{ fixEmailLink }} />
          </p>
        </div>
      </div>
    );

    const termsAndConditions = (
      <div className={css.termsAndConditionsContainer}>
        <h1>
          <FormattedMessage id="TermsAndConditionsPage.heading" />
        </h1>
        <TermsAndConditions className={css.termsWrapper} />
        <CloseButton onClick={() => this.setState({ tosModalOpen: false })} />
      </div>
    );

    const privacyPolicy = (
      <div className={css.termsAndConditionsContainer}>
        <h1>
          <FormattedMessage id="General.privacyPolicy" />
        </h1>
        <PrivacyPolicy className={css.termsWrapper} />
        <CloseButton onClick={() => this.setState({ privacyModalOpen: false })} />
      </div>
    );

    const siteTitle = config.marketplaceName;
    const schemaTitle = isLogin
      ? intl.formatMessage({ id: 'AuthenticationPage.schemaTitleLogin' }, { siteTitle })
      : intl.formatMessage({ id: 'AuthenticationPage.schemaTitleSignup' }, { siteTitle });

    const topbarClasses = classNames({
      [css.hideOnMobile]: showEmailVerification,
    });

    let renderContent = null;
    if (this.state.tosModalOpen) {
      renderContent = termsAndConditions;
    } else if (this.state.privacyModalOpen) {
      renderContent = privacyPolicy;
    } else if (showEmailVerification) {
      renderContent = emailVerificationContent;
    } else {
      renderContent = formContent;
    }

    return (
      <div className={css.pageWrapper}>
        <div 
          className={css.root}
          onClick={(e) => {
            // Close modal when clicking on backdrop (not the content)
            if (e.target === e.currentTarget) {
              const backgroundLocation = location.state?.backgroundLocation;
              if (backgroundLocation) {
                // Navigate directly to background location, not history.goBack()
                history.push(backgroundLocation.pathname + backgroundLocation.search);
              } else {
                history.push('/');
              }
            }
          }}
        >
          {renderContent}
        </div>
      </div>
    );
  }
}

AuthenticationPageComponent.defaultProps = {
  currentUser: null,
  loginError: null,
  signupError: null,
  confirmError: null,
  tab: 'signup',
  sendVerificationEmailError: null,
  showSocialLoginsForTests: false,
};

const { bool, func, object, oneOf, shape } = PropTypes;

AuthenticationPageComponent.propTypes = {
  authInProgress: bool.isRequired,
  currentUser: propTypes.currentUser,
  isAuthenticated: bool.isRequired,
  loginError: propTypes.error,
  scrollingDisabled: bool.isRequired,
  signupError: propTypes.error,
  confirmError: propTypes.error,

  submitLogin: func.isRequired,
  submitSignup: func.isRequired,
  submitSingupWithIdp: func.isRequired,
  tab: oneOf(['login', 'signup', 'confirm']),

  sendVerificationEmailInProgress: bool.isRequired,
  sendVerificationEmailError: propTypes.error,
  onResendVerificationEmail: func.isRequired,
  onManageDisableScrolling: func.isRequired,

  // from withRouter
  location: shape({ state: object }).isRequired,
  history: shape({
    goBack: func,
    push: func.isRequired,
    replace: func.isRequired,
  }).isRequired,
  config: object.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { isAuthenticated, loginError, signupError, confirmError } = state.auth;
  const { currentUser, sendVerificationEmailInProgress, sendVerificationEmailError } = state.user;
  return {
    authInProgress: authenticationInProgress(state),
    currentUser,
    isAuthenticated,
    loginError,
    scrollingDisabled: isScrollingDisabled(state),
    signupError,
    confirmError,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
  };
};

const mapDispatchToProps = dispatch => ({
  submitLogin: ({ email, password }) => dispatch(login(email, password)),
  submitSignup: params => dispatch(signup(params)),
  submitSingupWithIdp: params => dispatch(signupWithIdp(params)),
  onResendVerificationEmail: () => dispatch(sendVerificationEmail()),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

const AuthenticationPageWrapper = props => {
  const config = useConfiguration();

  return <AuthenticationPageComponent {...props} config={config} />;
};

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const AuthenticationPage = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  injectIntl
)(AuthenticationPageWrapper);

export default AuthenticationPage;
